import React, { useState, type HTMLAttributes, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { RequestOptions } from 'searchkit';
import { useTranslation } from 'react-i18next';

import {
  SearchkitWrapper,
  SearchResultsHeadless,
  SearchResultsList,
  SearchPagination,
  SearchResultsInfinite,
  SearchBar,
} from '@uncinc/react-searchkit';
import { NavigationScroll } from '@uncinc/uncinc-react-kitchen-sink';


import type {
  MetaParagraphsContentTypes,
} from '../../../types/uncinc_profile/paragraphs';
import type {
  SearchAllContentArticleHit,
  SearchAllContentHit,
  SearchAllContentProjectHit,
  SearchAllContentVacancyHit,
  SearchAllContentStoryHit,
  SearchAllContentAvOPreviousYearsHit,
  SearchAllContentAvoProjectHit,
} from '../../SearchAllContent/types';
import { showMetaProjectFilters, hideMetaProjectFilters } from '../../../actions/UI';

import MetaSearchFacets from './Facets';
import MetaSorting from './MetaSorting';

import { APP_OPTIONS, SEARCHKIT_HOST_URL } from '../../../config/constants';
import { facetAttributes, resultAttributes } from './config';
import { sortByCustomOrder } from './metaParagraphsHelpers';

import ArticleHit from './Results/ArticleHit';
import ProjectHit from './Results/ProjectHit';
import AvOProjectHit from './Results/AvOProjectHit';
import VacancyHit from './Results/VacancyHit';
import StoryHit from './Results/StoryHit';
import AvOPreviousYearsHit from './Results/AvOPreviousYearsHit';
import Button from '../../Button';

const PAGINATION_RANGE_DISPLAYED = 2;

/**
 * Renders the appropriate hit component based on content type
 * @param hit - Search hit containing content data
 * @returns React component for the specific content type
 */
const RenderItem = (hit: SearchAllContentHit) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { content_type, profile_type } = hit;

  // Determine the type to render (priority: profile_type > content_type)
  const type = profile_type?.[0] ? profile_type[0] : content_type[0];

  switch (type) {
    case 'article': {
      return <ArticleHit hit={hit as SearchAllContentArticleHit} />;
    }
    case 'project': {
      return <ProjectHit hit={hit as SearchAllContentProjectHit} />;
    }
    case 'vacancy': {
      return <VacancyHit hit={hit as SearchAllContentVacancyHit} />;
    }
    case 'story': {
      return <StoryHit hit={hit as SearchAllContentStoryHit} />;
    }
    case 'avo_previous_years_page': {
      return <AvOPreviousYearsHit hit={hit as SearchAllContentAvOPreviousYearsHit} />;
    }
    case 'avo_project': {
      return <AvOProjectHit hit={hit as SearchAllContentAvoProjectHit} />;
    }
    default: {
      return <div />;
    }
  }
};

/**
 * Props for the MetaSearch component
 */
interface MetaSearchProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  /** Searchkit request options for customizing queries */
  requestOptions: RequestOptions;
  /** Content type to determine search behavior and available features */
  contentType: MetaParagraphsContentTypes;
  /** Default sort field */
  sortField?: string;
  /** Default sort direction */
  sortDirection?: 'desc' | 'asc';
  /** Component to show when no results are found */
  emptyState?: React.ReactElement;
  /** Custom sorting order for results (array of IDs) */
  sortingList?: number[];
  /** Enable infinite scrolling instead of pagination */
  infinite?: boolean;
  /** Number of results per page/load */
  hitsPerPage: number;
  /** Show search facets */
  withFacets?: boolean;
  /** Show pagination controls (ignored when infinite=true) */
  withPagination?: boolean;
  /** Enable content-type specific sorting options */
  withSorting?: boolean;
  /** Show search bar */
  withSearchBar?: boolean;
  /** Custom component for rendering result lists */
  ResultListComponent?: React.FC;
}

/**
 * MetaSearch component provides a flexible search interface with content-type specific features
 *
 * Features:
 * - Content-type aware result rendering
 * - Optional search bar, facets, sorting, and pagination
 * - Support for infinite scrolling
 * - Random sorting with seeded consistency (for avo_project content type)
 * - Custom sorting order support
 */
const MetaSearch: React.FC<MetaSearchProps> = ({
  requestOptions,
  contentType,
  sortingList = undefined,
  sortField = null,
  sortDirection = 'desc',
  emptyState,
  infinite = false,
  hitsPerPage,
  withFacets = false,
  withPagination = false,
  withSorting = false,
  withSearchBar = false,
  ResultListComponent = SearchResultsList,
}) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
  const handleMobileMenuClick = () => {
    if (mobileMenuIsOpen) {
      // @ts-ignore
      dispatch(hideMetaProjectFilters());
      setMobileMenuIsOpen(false);
    } else {
      // @ts-ignore
      dispatch(showMetaProjectFilters());
      setMobileMenuIsOpen(true);
    }
  };
  // Generate a consistent random seed for random sorting to prevent duplicates in pagination
  const randomSeed = useMemo(() => Math.floor(Math.random() * 1000000), []);

  // Track the currently active sort option
  const [activeSort, setActiveSort] = useState(sortField);

  /**
   * Determines the sort field based on active sort selection
   * Handles content-type specific defaults
   */
  const getSortField = () => {
    if (activeSort === '_score') return '_score';
    if (activeSort === 'title_asc') return 'title';
    if (activeSort === 'title_desc') return 'title';
    return sortField || ((contentType === 'article' || contentType === 'vacancy') ? 'publish_date' : 'created');
  };

  /**
   * Determines the sort direction based on active sort selection
   */
  const getSortDirection = () => {
    if (activeSort === 'title_asc') return 'asc';
    if (activeSort === 'title_desc') return 'desc';
    return sortDirection;
  };

  /**
   * Creates content-type specific request options
   * Handles special cases like random sorting for avo_project
   */
  const getRequestOptions = () => {
    // Special handling for random sorting (avo_project only)
    if (withSorting && activeSort === 'random') {
      return {
        ...requestOptions,
        hooks: {
          ...requestOptions.hooks,
          beforeSearch: (requests: any) => Promise.resolve(requests.map((request: any) => ({
            ...request,
            body: {
              ...request.body,
              query: {
                function_score: {
                  query: request.body.query || { match_all: {} },
                  random_score: {
                    seed: randomSeed,
                  },
                },
              },
              sort: [{ _score: { order: 'desc' } }],
            },
          }))),
        },
      };
    }

    return requestOptions;
  };

  return (
    <div className="MetaSearch">
      <SearchkitWrapper
        connection={{
          host: SEARCHKIT_HOST_URL,
        }}
        indexName={APP_OPTIONS?.parameters.searchkitIndexAllContent}
        routing={false}
        hitsPerPage={hitsPerPage}
        requestOptions={getRequestOptions()}
        searchSettings={{
          search_attributes: [],
          result_attributes: resultAttributes,
          facet_attributes: facetAttributes,
          sorting: {
            default: {
              field: getSortField(),
              order: getSortDirection(),
            },
          },
        }}
      >
        <div className="ParagraphMeta__wrapper">
          {/* Search controls section */}
          <div className="ParagraphMeta__wrapper__top">
            {withSearchBar && (
              <SearchBar
                title={t('meta_search.projects_overview.search.search_bar_title')}
                placeholder={t('meta_search.projects_overview.search.search_bar_placeholder')}
                buttonText={t('meta_search.projects_overview.search.search_bar_button_title')}
              />
            )}

            <div className="ParagraphMeta__wrapper__top__filters">
              {withFacets && (
                <div className="ParagraphMeta__facets">
                  <MetaSearchFacets />
                </div>
              )}

              {withSorting && (
                <div className="ParagraphMeta__sorting">
                  <label htmlFor="sort-select-search">
                    {t('meta_search.projects_overview.sorting.label')}
                  </label>
                  <MetaSorting
                    contentType={contentType}
                    activeSort={activeSort}
                    onSortChange={setActiveSort}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="ParagraphMeta__wrapper__top__mobile">
            <Button primary inverted withIcon icon="filter" onClick={handleMobileMenuClick}>
              Filteren
            </Button>
          </div>
          <div className="ParagraphMeta__wrapper__top__mobile__menu">
            <NavigationScroll>
              <Button textual onClick={handleMobileMenuClick}>
                <i className="icon icon-arrow-left" />
                {t('cancel')}
              </Button>
              <div className="ParagraphMeta__wrapper__top__mobile__menu__filters">
                {withSearchBar && (
                  <SearchBar
                    onSubmit={handleMobileMenuClick}
                    title={t('meta_search.projects_overview.search.search_bar_title')}
                    placeholder={t('meta_search.projects_overview.search.search_bar_placeholder')}
                    buttonText={t('meta_search.projects_overview.search.search_bar_button_title')}
                  />
                )}

                {withFacets && (
                  <div className="ParagraphMeta__facets">
                    <MetaSearchFacets />
                  </div>
                )}

                {withSorting && (
                  <div>
                    <label htmlFor="sort-select-search">
                      {t('meta_search.projects_overview.sorting.label')}
                    </label>
                    <MetaSorting
                      contentType={contentType}
                      activeSort={activeSort}
                      onSortChange={setActiveSort}
                    />
                  </div>
                )}
              </div>
            </NavigationScroll>
          </div>

          {/* Results section */}
          <div className="ParagraphMeta__results ParagraphHighlightedSelection__results">
            {infinite ? (
              <SearchResultsInfinite
                key={`infinite-results-${activeSort || 'default'}`}
                renderItem={RenderItem}
              />
            ) : (
              <SearchResultsHeadless>
                {(hits) => {
                  // Apply custom sorting if provided
                  const sortedItems = sortingList ? sortByCustomOrder(hits, sortingList) : hits;

                  return (
                    <ResultListComponent
                      renderItem={RenderItem}
                      hits={sortedItems}
                      emptyState={emptyState}
                    />
                  );
                }}
              </SearchResultsHeadless>
            )}

            {/* Pagination (only shown when not using infinite scroll) */}
            {withPagination && !infinite && (
              <SearchPagination pageRangeDisplayed={PAGINATION_RANGE_DISPLAYED} />
            )}
          </div>
        </div>
      </SearchkitWrapper>
    </div>
  );
};

export default MetaSearch;
